import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { 
  Box, 
  Typography, 
  Container, 
  useTheme, 
  alpha, 
  Divider,
  Button,
  Grid2,
  Card,
  CardContent,
  CardActions,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BasePage from '../pages/BasePage';
import { SAVED_HOME_LIMITS, MONTHLY_WALKTHROUGH_LIMITS } from '../../shared/constants';

// Production settings
const PRODUCTION_CLIENT_TOKEN = 'live_baf5fbb8984d4edae23c12eb957';
const PRODUCTION_PRICE_ID = 'pri_01jsstaqkdhdkkajyha9q2km7d';

// Sandbox settings
const SANDBOX_CLIENT_TOKEN = 'test_eb0ae224a20290630f6d56f51e7';
const SANDBOX_PRICE_ID = 'pri_01jtbkppbsw5yrgsqxksgye29n';

function PricingPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Add this to access URL query params
  const theme = useTheme();
  const [paddle, setPaddle] = useState<Paddle | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'idle' | 'success' | 'error' | 'closed'>('idle');
  
  // Extract transaction ID from URL if present
  const [transactionId, setTransactionId] = useState<string | null>(null);
  
  // Add state for price information
  const [priceInfo, setPriceInfo] = useState<{
    formattedPrice: string;
    isLoading: boolean;
    error: string | null;
  }>({
    formattedPrice: '$12',  // Default fallback price
    isLoading: true,
    error: null
  });
  
  // Determine environment based on domain
  const isProduction = window.location.hostname === 'cribcritic.ai';
  const paddleEnvironment = isProduction ? 'production' : 'sandbox';
  const paddleClientToken = isProduction ? PRODUCTION_CLIENT_TOKEN : SANDBOX_CLIENT_TOKEN;
  const premiumPriceId = isProduction ? PRODUCTION_PRICE_ID : SANDBOX_PRICE_ID;

  // Initialize Paddle when component mounts
  useEffect(() => {
    const setupPaddle = async () => {
      try {
        console.log(`Initializing Paddle in ${paddleEnvironment} environment`);
        const paddleInstance = await initializePaddle({ 
          environment: paddleEnvironment, 
          token: paddleClientToken,
          eventCallback: (event) => {
            console.log('Paddle event:', event.name, event);
            
            if (event.name === 'checkout.completed') {
              console.log('Checkout succeeded:', event.data);
              setCheckoutStatus('success');
              setIsLoading(false);
              
              // You might want to call your backend API to verify the subscription
              // and update the user's subscription status
              
              // Redirect to a thank you page or dashboard after a short delay
              setTimeout(() => {
                navigate('/account', { state: { subscriptionSuccess: true } });
              }, 1500);
            } else if (event.name === 'checkout.error') {
              console.error('Checkout error:', event.data);
              setCheckoutStatus('error');
              setIsLoading(false);
            } else if (event.name === 'checkout.closed') {
              console.log('Checkout closed by user');
              setCheckoutStatus('closed');
              setIsLoading(false);
            }
          }
        });
        
        if (paddleInstance) {
          setPaddle(paddleInstance);
          console.log('Paddle initialized successfully');
        }
      } catch (error) {
        console.error('Failed to initialize Paddle:', error);
      }
    };
    
    setupPaddle();
  }, [paddleEnvironment, paddleClientToken, navigate]);

  // Extract transaction ID from query params when component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const txnParam = searchParams.get('_ptxn');
    if (txnParam) {
      setTransactionId(txnParam);
    }
  }, [location.search]);

  // Open checkout with transaction ID if available and Paddle is initialized
  useEffect(() => {
    const openCheckoutWithTransaction = async () => {
      if (!paddle || !transactionId || !isAuthenticated) return;
      
      try {
        setIsLoading(true);
        console.log(`Opening checkout with transaction ID: ${transactionId}`);
        
        await paddle.Checkout.open({
          transactionId,
          // Note: eventCallback is already handled in the initializePaddle call
        });
      } catch (error) {
        console.error('Error opening checkout with transaction ID:', error);
        setIsLoading(false);
        setCheckoutStatus('error');
      }
    };
    
    if (transactionId && paddle) {
      openCheckoutWithTransaction();
    }
  }, [paddle, transactionId, isAuthenticated]);

  // Fetch price information after Paddle is initialized
  useEffect(() => {
    const fetchPricePreview = async () => {
      if (!paddle) return;
      
      try {
        setPriceInfo(prev => ({ ...prev, isLoading: true, error: null }));
        
        const pricePreview = await paddle.PricePreview({
          items: [
            { priceId: premiumPriceId, quantity: 1 }
          ],
        });
        
        console.log('Price preview result:', pricePreview);
        
        // Corrected path: formattedTotals is directly on the lineItem, not under price
        if (pricePreview?.data?.details?.lineItems?.[0]?.formattedTotals?.total) {
          const formattedPrice = pricePreview.data.details.lineItems[0].formattedTotals.total;
          setPriceInfo({
            formattedPrice, // Directly use pre-formatted string ("$10.00")
            isLoading: false,
            error: null
          });
        } else {
          throw new Error('Invalid price data received');
        }
      } catch (error) {
        console.error('Error fetching price preview:', error);
        setPriceInfo(prev => ({ 
          ...prev, 
          isLoading: false, 
          error: 'Could not load current pricing' 
        }));
      }
    };
    
    if (paddle) {
      fetchPricePreview();
    }
  }, [paddle, premiumPriceId]);

  // Reset checkout status when it's 'closed' or 'error' after a delay
  useEffect(() => {
    if (checkoutStatus === 'closed' || checkoutStatus === 'error') {
      const timer = setTimeout(() => {
        setCheckoutStatus('idle');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [checkoutStatus]);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/homes');
    } else {
      navigate('/login');
    }
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: '/pricing' } });
      return;
    }

    if (!paddle) {
      console.error('Paddle is not initialized');
      return;
    }

    setIsLoading(true);
    
    try {
      // If we have a transaction ID, use it; otherwise use items
      if (transactionId) {
        await paddle.Checkout.open({
          transactionId,
        });
      } else {
        await paddle.Checkout.open({
          items: [{ priceId: premiumPriceId, quantity: 1 }],
          customer: {
            email: user?.email || '',
            // You can add more customer data here if available
          },
        });
      }
    } catch (error) {
      console.error('Error opening checkout:', error);
      setIsLoading(false);
      setCheckoutStatus('error');
    }
  };

  return (
    <BasePage
      title="Pricing | Crib Critic AI"
      description="Choose the right CribCritic plan to help you make smarter home buying decisions with AI-powered property insights."
      keywords="pricing, subscription, home evaluation, AI property analysis"
      canonicalUrl="https://cribcritic.ai/pricing"
    >
      <Box sx={{
        py: { xs: 8, md: 12 },
        background: theme => theme.palette.mode === 'dark' 
          ? `linear-gradient(145deg, ${alpha(theme.palette.background.primary, 0.6)} 0%, ${alpha(theme.palette.background.secondary, 0.9)} 100%)`
          : `linear-gradient(145deg, ${alpha(theme.palette.background.default, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        backdropFilter: 'blur(8px)',
      }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Simple, Transparent Pricing
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ maxWidth: 700, mx: 'auto' }}
            >
              Choose the plan that's right for your home buying journey
            </Typography>
          </Box>

          {/* Pricing Cards */}
          <Grid2 container spacing={4} justifyContent="center">
            {/* Free Plan */}
            <Grid2 size={{xs:12, md:6, lg:5}}>
              <Card 
                elevation={2} 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 30px -10px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box 
                  sx={{ 
                    // Remove the explicit background color
                    // bgcolor: 'background.paper', 
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    // Add a subtle gradient background that blends with the card
                    background: theme => `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.4)} 0%, transparent 100%)`,
                    borderTopLeftRadius: 12,   // Match the card's border radius
                    borderTopRightRadius: 12   // Match the card's border radius
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 36, color: theme.palette.primary.main, mr: 2 }} />
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    Free Plan
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                  <Typography 
                    variant="h4" 
                    component="div" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center' // Add this line to center the pricing
                    }}
                  >
                    $0 
                    <Typography variant="subtitle1" component="span" color="text.secondary" sx={{ ml: 1 }}>
                      / month
                    </Typography>
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <List>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={`${MONTHLY_WALKTHROUGH_LIMITS.FREE} home walkthroughs per month`} />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={`Store up to ${SAVED_HOME_LIMITS.FREE} saved homes`} />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="AI-powered home analysis" />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Basic property insights" />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 1 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Get Started
                  </Button>
                </CardActions>
              </Card>
            </Grid2>
            
            {/* Premium Plan */}
            <Grid2 size={{xs:12, md:6, lg:5}}>
              <Card 
                elevation={4} 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  position: 'relative',
                  border: `2px solid ${theme.palette.secondary.main}`,
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 30px -10px ${alpha(theme.palette.secondary.main, 0.25)}`
                  }
                }}
              >
                <Box 
                  sx={{ 
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
                  }}
                >
                  <WorkspacePremiumIcon sx={{ fontSize: 36, color: theme.palette.secondary.main, mr: 2 }} />
                  <Typography variant="h5" component="h2" fontWeight={600}>
                    Premium Plan
                  </Typography>
                </Box>
                <CardContent sx={{ flexGrow: 1, pt: 4 }}>
                  {/* Update the Premium Plan price display */}
                  <Typography 
                    variant="h4" 
                    component="div" 
                    sx={{ 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'center'
                    }}
                  >
                    {priceInfo.isLoading ? (
                      <CircularProgress size={24} color="secondary" />
                    ) : priceInfo.error ? (
                      <Typography color="error" variant="body2">{priceInfo.formattedPrice}</Typography>
                    ) : (
                      <>
                        {priceInfo.formattedPrice}
                        <Typography variant="subtitle1" component="span" color="text.secondary" sx={{ ml: 1 }}>
                          / month
                        </Typography>
                      </>
                    )}
                  </Typography>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  <List>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary={`${MONTHLY_WALKTHROUGH_LIMITS.PRO} home walkthroughs per month`} />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary={`Store up to ${SAVED_HOME_LIMITS.PRO} saved homes`} />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary="AI-powered home analysis" />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary="Advanced property insights" />
                    </ListItem>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <CheckCircleOutlineIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText primary="Priority support" />
                    </ListItem>
                  </List>
                </CardContent>
                <CardActions sx={{ p: 3, pt: 1 }}>
                  {/* Premium Plan button with status feedback */}
                  <Button 
                    fullWidth 
                    variant="contained" 
                    color="secondary"
                    size="large"
                    onClick={handleUpgrade}
                    disabled={isLoading || !paddle}
                    sx={{
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : checkoutStatus === 'success' ? (
                      'Subscription Activated!'
                    ) : checkoutStatus === 'error' ? (
                      'Try Again'
                    ) : (
                      'Upgrade Now'
                    )}
                  </Button>
                  
                  {/* Show status messages beneath the button */}
                  {checkoutStatus === 'error' && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      There was an error with your payment. Please try again.
                    </Typography>
                  )}
                  {checkoutStatus === 'closed' && (
                    <Typography color="text.secondary" variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      Checkout was closed. You can try again anytime.
                    </Typography>
                  )}
                </CardActions>
              </Card>
            </Grid2>
          </Grid2>
          
          {/* FAQ or additional information */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Typography variant="body1" color="text.secondary">
              Have questions about our plans?{' '}
              <Button
                component={RouterLink}
                to="/contact"
                variant="text"
                sx={{ fontWeight: 600 }}
              >
                Contact us
              </Button>
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}
            >
              <Link component={RouterLink} to="/refunds" underline="hover">
                Refund Policy
              </Link>
              <Link component={RouterLink} to="/terms-of-service" underline="hover">
                Terms of Service
              </Link>
              <Link component={RouterLink} to="/privacy-policy" underline="hover">
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Container>
      </Box>
    </BasePage>
  );
}

export default PricingPage;